/**
 * OptionsWindow — matches DefineSprite_376_optionsWindow.
 *
 *   ┌── Options ─────────────────────────────────[×]┐
 *   │  ☐ Show Timestamps        ☐ Play Effects      │
 *   │  ☐ Auto-scroll Bottom     ☐ Play Sounds       │
 *   │  ☐ Mac <> Fix             [color: ______]     │
 *   │                                                │
 *   │  OS: <browser>     2.9.9         [ OK ]       │
 *   └────────────────────────────────────────────────┘
 *
 * "Mac <> Fix" was a SWF UTF-8 workaround; here it's a faithful no-op
 * (the checkbox flips, persists, but does nothing).
 */
import { useState } from 'react';
import { Modal } from './Modal.js';
import { usePrefs } from '../hooks/usePrefs.js';
import { STATUS_OPTIONS } from '../lib/status.js';

const VERSION = '10.0 (web)';      // matches "2.9.9" placement in the SWF

function osLabel(): string {
    const ua = navigator.userAgent;
    if (/Win/i.test(ua))    return 'Windows';
    if (/Mac/i.test(ua))    return 'macOS';
    if (/Linux/i.test(ua))  return 'Linux';
    if (/Android/i.test(ua))return 'Android';
    if (/iPhone|iPad/i.test(ua)) return 'iOS';
    return 'N/A';
}

interface Props {
    onClose: () => void;
    /** Setting your own status fires a `user_status` cmd. */
    onSetStatus?: (s: number) => void;
    /** Current uStatus to preselect. */
    currentStatus?: number;
    /** Open the colour-shop preset picker. */
    onOpenColorShop?: () => void;
}

export function OptionsWindow({ onClose, onSetStatus, currentStatus = 0, onOpenColorShop }: Props) {
    const [prefs, setPref] = usePrefs();
    const [colour, setColour] = useState(prefs.colorOverride ?? '');
    const [status, setStatus] = useState(currentStatus);

    return (
        <Modal title="Options" onClose={onClose} className="pc-modal-options">
            <div className="opt-grid">
                <label className="opt-row">
                    <input type="checkbox" checked={prefs.showTimestamps}
                           onChange={e => setPref('showTimestamps', e.target.checked)} />
                    Show Timestamps
                </label>
                <label className="opt-row">
                    <input type="checkbox" checked={prefs.playEffects}
                           onChange={e => setPref('playEffects', e.target.checked)} />
                    Play Effects
                </label>
                <label className="opt-row">
                    <input type="checkbox" checked={prefs.autoscrollBottom}
                           onChange={e => setPref('autoscrollBottom', e.target.checked)} />
                    Auto-scroll Bottom
                </label>
                <label className="opt-row">
                    <input type="checkbox" checked={prefs.playSounds}
                           onChange={e => setPref('playSounds', e.target.checked)} />
                    Play Sounds
                </label>
                <label className="opt-row">
                    <input type="checkbox" checked={prefs.macFix}
                           onChange={e => setPref('macFix', e.target.checked)} />
                    Mac &lt;&gt; Fix
                </label>
                <label className="opt-row opt-row-color">
                    <span>colour:</span>
                    <input type="text" maxLength={7}
                           placeholder="#rrggbb or empty"
                           value={colour}
                           onChange={e => setColour(e.target.value)}
                           onBlur={() => {
                               const v = colour.trim();
                               if (v === '') { setPref('colorOverride', null); return; }
                               if (/^#[0-9a-fA-F]{6}$/.test(v)) setPref('colorOverride', v);
                           }} />
                    {onOpenColorShop && (
                        <button type="button" className="opt-color-shop-btn"
                                onClick={() => onOpenColorShop()}>Pick…</button>
                    )}
                </label>
                {onSetStatus && (
                    <label className="opt-row opt-row-status">
                        <span>status:</span>
                        <select value={status}
                                onChange={e => {
                                    const v = Number(e.target.value);
                                    setStatus(v);
                                    onSetStatus(v);
                                }}>
                            {STATUS_OPTIONS.map((o, i) => (
                                <option key={i} value={i}>● {o.label}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
            <div className="opt-foot">
                <span className="opt-os">OS: {osLabel()}</span>
                <span className="opt-version">{VERSION}</span>
                <button className="pw-btn" onClick={onClose}>OK</button>
            </div>
        </Modal>
    );
}
